/**
 * Is the current session an API-key ("BytePlus key") account?
 *
 * The ONE copy of this check (Pattern #99). It lives in a leaf module, not in
 * api-helper.js, because clientTelemetry needs it and api-helper imports
 * clientTelemetry — importing back would create a cycle. api-helper re-exports it.
 *
 * Two sources, because a restored session may carry only one of them:
 *  - sessionStorage `isApiKeyAccount === 'true'` (set by the api-key login), and
 *  - the persisted `localStorage.account` record with `isApiKeyAccount === true`
 *    (survives a browser restart; the api-key login also writes the key to
 *    localStorage, so the session flag alone would miss a restored session).
 * Deliberately NOT `apiKeyEmail`: nothing ever clears it, so it would misclassify
 * a later normal login.
 *
 * An API-key account's stored `apiKey` is a BytePlus credential. It is never a valid
 * Promptus credential and must never be sent to the Promptus API.
 *
 * @param {Storage|null} [session] defaults to window.sessionStorage (tests/telemetry inject)
 * @param {Storage|null} [storage] defaults to window.localStorage
 * @returns {boolean}
 */
// Parse cache for the persisted account record. This check runs on every Promptus
// request (incl. 2.5s progress polling) and the record can be large, so it is only
// re-parsed when the raw localStorage string changes.
let lastAccountRaw;
let lastAccountIsApiKey = false;

const accountRecordIsApiKey = (raw) => {
    if (raw === lastAccountRaw) return lastAccountIsApiKey;
    let result = false;
    try {
        const acct = JSON.parse(raw || 'null');
        result = !!(acct && acct.isApiKeyAccount === true);
    } catch (e) { /* unparseable account record: not an API-key account */ }
    lastAccountRaw = raw;
    lastAccountIsApiKey = result;
    return result;
};

export const isApiKeyAccountSession = (session, storage) => {
    try {
        const hasWindow = typeof window !== 'undefined';
        const ss = session !== undefined ? session : (hasWindow ? window.sessionStorage : null);
        const ls = storage !== undefined ? storage : (hasWindow ? window.localStorage : null);
        if (ss && ss.getItem('isApiKeyAccount') === 'true') return true;
        if (ls && accountRecordIsApiKey(ls.getItem('account'))) return true;
        return false;
    } catch (e) {
        return false;
    }
};
